<script setup lang="ts">
import { useUiStore, type IframeWindow } from '@/stores/ui';
import {
  XMarkIcon,
  MinusIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/vue/24/outline';

const uiStore = useUiStore();

function handleClose(iframe: IframeWindow) {
  uiStore.closeIframeWindow(iframe.id);
}

function handleMinimize(iframe: IframeWindow) {
  uiStore.minimizeIframeWindow(iframe.id);
}

function handleOpenInNewTab(iframe: IframeWindow) {
  uiStore.openInNewTab(iframe.id);
}
</script>

<template>
  <Teleport to="body">
    <!-- Container for ALL iframes - always mounted to preserve state -->
    <div
      v-for="iframe in uiStore.iframeWindows"
      :key="iframe.id"
      class="fixed inset-0 z-50 flex flex-col transition-all duration-200"
      :class="iframe.minimized ? 'pointer-events-none opacity-0 invisible' : 'pointer-events-auto opacity-100 visible'"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm"
        @click="handleMinimize(iframe)"
      />

      <!-- Modal Window -->
      <div
        class="relative flex flex-col m-3 sm:m-4 lg:m-6 flex-1 max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-3rem)] transition-transform duration-200"
        :class="iframe.minimized ? 'scale-95' : 'scale-100'"
      >
        <!-- Title bar -->
        <div class="flex items-center justify-between px-4 py-2 bg-gray-800 dark:bg-dark-800 rounded-t-xl border-b border-gray-700">
          <div class="flex items-center gap-3 min-w-0">
            <!-- Favicon -->
            <img
              v-if="iframe.faviconUrl"
              :src="iframe.faviconUrl"
              :alt="iframe.title"
              class="w-5 h-5 rounded"
              @error="($event.target as HTMLImageElement).style.display = 'none'"
            >
            <div
              v-else
              class="w-5 h-5 bg-gray-600 rounded flex items-center justify-center"
            >
              <span class="text-xs font-medium text-white">
                {{ iframe.title.charAt(0).toUpperCase() }}
              </span>
            </div>

            <!-- Title -->
            <span class="text-sm font-medium text-white truncate">
              {{ iframe.title }}
            </span>

            <!-- URL indicator -->
            <span class="hidden sm:inline text-xs text-gray-400 truncate max-w-[200px] lg:max-w-[400px]">
              {{ iframe.url }}
            </span>
          </div>

          <!-- Controls -->
          <div class="flex items-center gap-1">
            <!-- Open in new tab -->
            <button
              class="p-2 hover:bg-gray-700 rounded-lg transition-colors group"
              title="Ouvrir dans un nouvel onglet"
              @click="handleOpenInNewTab(iframe)"
            >
              <ArrowTopRightOnSquareIcon class="w-4 h-4 text-gray-400 group-hover:text-white" />
            </button>

            <!-- Minimize -->
            <button
              class="p-2 hover:bg-gray-700 rounded-lg transition-colors group"
              title="Réduire (Échap)"
              @click="handleMinimize(iframe)"
            >
              <MinusIcon class="w-4 h-4 text-gray-400 group-hover:text-yellow-400" />
            </button>

            <!-- Close -->
            <button
              class="p-2 hover:bg-red-600 rounded-lg transition-colors group"
              title="Fermer"
              @click="handleClose(iframe)"
            >
              <XMarkIcon class="w-4 h-4 text-gray-400 group-hover:text-white" />
            </button>
          </div>
        </div>

        <!-- Iframe container - iframe is always mounted -->
        <div class="flex-1 bg-white dark:bg-dark-900 rounded-b-xl overflow-hidden">
          <iframe
            :src="iframe.url"
            :title="iframe.title"
            class="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
